import React from "react";
import {
  MoreVertical,
  ChevronLast,
  ChevronFirst,
  LayoutDashboard,
  Phone,
  MessageSquare,
  Send,
  BotMessageSquare,
  Bot,
  Hotel,
  Banknote,
  Users,
  Search,
  ChevronDown,
  Settings,
  Package,
  Video,
} from "lucide-react";
import { useContext, createContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { t, getLang } from "../i18n"; // <-- import i18n

const SidebarContext = createContext();

export default function Sidebar({ children, open = false, onToggle }) {
  const expanded = open;
  const location = useLocation();
  const lang = getLang();
  const isRTL = lang === "ar";
  const [searchValue, setSearchValue] = React.useState("");

  // All sidebar links as objects for filtering
  const sidebarLinks = [
    { section: "الرئيسية", items: [
      { icon: <LayoutDashboard size={20} />, text: t("dashboard"), to: "/admin/", active: location.pathname === "/admin/" },
    ]},
    { section: "الإدارة", items: [
      { icon: <Users size={20} />, text: "الأعضاء", to: "/admin/members", active: location.pathname === "/admin/members" },
      { icon: <Users size={20} />, text: t("users"), to: "/admin/users", active: location.pathname === "/admin/users" },
      { icon: <LayoutDashboard size={20} />, text: "السلايدر", to: "/admin/sliders", active: location.pathname === "/admin/sliders" },
      { icon: <Package size={20} />, text: "الباقات", to: "/admin/packages", active: location.pathname === "/admin/packages" },
      { icon: <Video size={20} />, text: "الدروس التعليمية", to: "/admin/learns", active: location.pathname === "/admin/learns" },
    ]},
    { section: "التقارير", items: [
      { icon: <Banknote size={20} />, text: "التقارير", to: "/admin/report", active: location.pathname === "/admin/report" },
    ]},
    { section: "المالية", items: [
      { icon: <Users size={20} />, text: "الموزعون", to: "/finance/resellers", active: location.pathname.startsWith("/finance/resellers") },
      { icon: <Banknote size={20} />, text: "الحركات المالية", to: "/finance/transactions", active: location.pathname.startsWith("/finance/transactions") },
      { icon: <Package size={20} />, text: "دفعات السندات", to: "/finance/voucher-batches", active: location.pathname.startsWith("/finance/voucher-batches") },
      { icon: <Banknote size={20} />, text: "المدفوعات", to: "/finance/payments", active: location.pathname.startsWith("/finance/payments") },
      { icon: <Settings size={20} />, text: "طرق الدفع", to: "/finance/payment-methods", active: location.pathname.startsWith("/finance/payment-methods") },
      { icon: <Banknote size={20} />, text: t("الحسابات"), to: "/admin/accounts", active: location.pathname === "/admin/accounts" },
    ]},
    { section: "الدعم", items: [
      { icon: <MessageSquare size={20} />, text: "الدعم", to: "/admin/support", active: location.pathname === "/admin/support" },
      { icon: <MessageSquare size={20} />, text: "تذاكر الدعم", to: "/admin/support/ticket", active: location.pathname === "/admin/support/ticket" },
    ]},
    { section: "النظام", items: [
      { icon: <Settings size={20} />, text: "الإعدادات", to: "/admin/settings", active: location.pathname === "/admin/settings" },
    ]},
  ];

  // Filter links by search value
  const filteredLinks = sidebarLinks.map(section => ({
    ...section,
    items: section.items.filter(item => item.text.toLowerCase().includes(searchValue.toLowerCase())),
  })).filter(section => section.items.length > 0);

  return (
    <aside
      className={`fixed top-0 ${
        isRTL ? "right-0" : "left-0"
      } transition-all duration-300 overflow-scroll z-30 ${
        expanded ? "w-64" : "w-16"
      }`}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <nav
        className={` flex flex-col h-screen overflow-scroll bg-white  ${
          isRTL ? "rounded-r-2xl" : "rounded-l-2xl"
        }`}
      >
        <div className="flex justify-center  items-center p-4"></div>
        {/* input with search */}
        <div className="p-5 relative ">
          <input
            type="text"
            placeholder="البحث"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            className="w-full text-gray-900 py-3 pl-10 pr-10 rounded-xl border border-gray-200 bg-gray-50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
          <Search
            className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400"
            size={20}
          />
        </div>
        <SidebarContext.Provider value={{ expanded }}>
          {filteredLinks.map(section => (
            <div key={section.section}>
              <h1 className="px-5 pb-2 font-bold text-[#1D2035]">{section.section}</h1>
              {section.items.map(item => (
                <SidebarItem key={item.to} {...item} />
              ))}
              <div className="mx-5 my-4 border-t border-gray-200 border-dashed" />
            </div>
          ))}
          {/* <ul className="flex-1 px-3 dark:text-gray-100">{children}</ul> */}
        </SidebarContext.Provider>
        <div className="border-t dark:border-gray-700 flex p-3 mt-auto">
          <img
            src={
              "https://ui-avatars.com/api/?background=2446ed&name=" +
              localStorage.getItem("user")
            }
            alt=""
            className="w-10 h-10 rounded-full"
          />
          <div
            className={`
              flex justify-between items-center w-full
              overflow-hidden transition-all ${
                expanded ? "mx-3" : "w-0"
              } dark:text-gray-100"
          `}
          >
            <div className="leading-4">
              <h4 className="font-semibold dark:text-gray-100 flex items-center gap-2">
                <span className="text-xs text-gray-500">المسؤول</span>
              </h4>
              <span className="text-sm text-gray-900 dark:text-gray-100 block">
                {localStorage.getItem("user")}
              </span>
            </div>
            <ChevronDown size={18} className="text-gray-400" />
          </div>
        </div>
      </nav>
    </aside>
  );
}

export function SidebarItem({ icon, text, active, alert, to }) {
  const { expanded } = useContext(SidebarContext);

  return (
    <Link to={to || "#"} className="block my-1">
      <li
        className={`
          relative flex items-center 
          font-medium rounded-lg cursor-pointer
          transition-colors group
          ${active ? " text-[#051fc4]" : "hover:bg-gray-50 text-[#1D2035]"}
          ${expanded ? "py-2 px-3" : "p-2 justify-center"}
        `}
      >
        {/* Icon square */}
        <div
          className={`flex items-center justify-center w-9 h-9 rounded-md border ss transition-colors ${
            active
              ? "bg-[var(--Primary-Primary)] text-white border-[var(--Primary-Primary)]"
              : "bg-white text-gray-700 border-gray-200"
          }`}
        >
          {icon}
        </div>
        <span
          className={`overflow-hidden transition-all ${
            expanded ? "w-52 mx-3" : "w-0"
          }`}
        >
          {text}
        </span>

        {/* Numeric alert badge */}
        {typeof alert === "number" && (
          <div className="absolute -left-2 top-1/2 -translate-y-1/2">
            <div className=" text-white text-xs font-bold rounded-md w-5 h-5 flex items-center justify-center">
              {alert}
            </div>
          </div>
        )}

        {/* Tooltip on collapse */}
        {!expanded && (
          <div
            className={`
              absolute left-full rounded-md px-2 py-1 ml-6
              bg-indigo-100 text-indigo-800 text-sm
              invisible opacity-20 -translate-x-3 transition-all
              group-hover:visible group-hover:opacity-100 group-hover:translate-x-0
              whitespace-nowrap
            `}
          >
            {text}
          </div>
        )}
      </li>
    </Link>
  );
}
<ul>
  {/* ...existing links... */}
  <li></li>
  {/* ...existing links... */}
</ul>;
